import type {
  IntelligenceFeedEngagementParam,
  IntelligenceFeedEngagementState,
  IntelligenceFeedListQuery,
  IntelligenceFeedListKind,
  IntelligenceFeedListResult,
} from "./IntelligenceFeedType";

const MOCK_ITEMS_ALL: IntelligenceFeedListResult["list"] = [
  {
    id: "intel-mock-pure-1",
    title: "行业政策例行吹风（纯情报示例）",
    summary:
      "相关部门召开例行吹风会，强调稳增长与结构优化并重，未涉及具体公司。本条目仅展示情报正文，无推荐与解读区。",
    occurredAt: "2026-05-10 09:30:00",
    importance: 6,
    type: 1,
    stocks: [],
    extraKind: "none",
    riskDetail: "",
    likeCount: 12,
    collectCount: 3,
    hasTeacherComment: false,
    teacherComment: "",
  },
  {
    id: "intel-mock-ai-stock",
    title: "龙头公司披露季度经营数据",
    summary: "某公司发布季度产销简报，核心产品线销量环比回升，产能利用率达近年高位。",
    occurredAt: "2026-05-10 11:15:22",
    importance: 8.5,
    type: 4,
    stocks: [
      {
        code: "600519",
        name: "贵州茅台",
        lastPrice: "1688.00",
        changePct: "+1.2%",
        fundamentalScore: 72,
        positionScore: 68,
        recommendReason: "现金流与定价权稳定，可作为防御性配置的观察样本（示例，非投资建议）。",
      },
    ],
    extraKind: "ai_stock",
    riskDetail: "",
    likeCount: 56,
    collectCount: 18,
    hasTeacherComment: false,
    teacherComment: "",
  },
  {
    id: "intel-mock-ai-risk",
    title: "海外市场波动加剧",
    summary: "主要指数隔夜下挫，避险情绪升温，人民币汇率短线波动。",
    occurredAt: "2026-05-09 22:40:00",
    importance: 7,
    type: 5,
    stocks: [],
    extraKind: "ai_risk",
    riskDetail:
      "外部流动性与风险偏好变化可能放大波动，注意仓位与回撤控制。本提示为风险说明示例，不构成任何操作依据。",
    likeCount: 33,
    collectCount: 9,
    hasTeacherComment: false,
    teacherComment: "",
  },
  {
    id: "intel-mock-teacher",
    title: "新能源链报价企稳",
    summary: "上游材料报价止跌，组件排产环比改善，产业链库存回到中性区间。",
    occurredAt: "2026-05-09 14:20:00",
    importance: 7.5,
    type: 6,
    stocks: [],
    extraKind: "none",
    riskDetail: "",
    likeCount: 41,
    collectCount: 11,
    hasTeacherComment: true,
    teacherComment:
      "短期更像修复反弹，中期仍看需求兑现节奏。仓位上建议分批观察，不追涨杀跌。（老师解读示例）",
    teacher: {
      staffId: "s-mock-1",
      realName: "王老师",
      nickname: "老王",
      avatarUrl: "https://picsum.photos/seed/intel-teacher/96/96",
      firstCommentAt: "2026-05-09 16:00:00",
    },
  },
];

const mockLikedIds = new Set<string>(["intel-mock-ai-stock"]);
const mockCollectedIds = new Set<string>(["intel-mock-teacher", "intel-mock-ai-risk"]);

function matchKeyword(row: (typeof MOCK_ITEMS_ALL)[0], kw: string): boolean {
  if (!kw.trim()) {
    return true;
  }
  const v = kw.trim().toLowerCase();
  return (
    row.title.toLowerCase().includes(v) || row.summary.toLowerCase().includes(v)
  );
}

function attachEngagement(
  row: (typeof MOCK_ITEMS_ALL)[0]
): IntelligenceFeedListResult["list"][0] {
  return {
    ...row,
    likedByMe: mockLikedIds.has(row.id),
    collectedByMe: mockCollectedIds.has(row.id),
  };
}

export async function mockIntelligenceFeedList(
  query: IntelligenceFeedListQuery,
  kind: IntelligenceFeedListKind
): Promise<IntelligenceFeedListResult> {
  const { spaceId, spaceProductId, keyword, pageNum, pageSize, type } = query;
  if (!spaceId?.trim() || !spaceProductId?.trim()) {
    return { total: 0, list: [] };
  }
  const kw = keyword?.trim() ?? "";
  let base = MOCK_ITEMS_ALL.filter((r) => matchKeyword(r, kw));
  if (type != null) {
    base = base.filter((r) => r.type === type);
  }
  if (query.minImportance != null) {
    base = base.filter((r) => r.importance >= query.minImportance!);
  }
  if (kind === "liked") {
    base = base.filter((r) => mockLikedIds.has(r.id));
  } else if (kind === "collected") {
    base = base.filter((r) => mockCollectedIds.has(r.id));
  }
  const total = base.length;
  const startIdx = Math.max(0, (pageNum - 1) * pageSize);
  const list = base.slice(startIdx, startIdx + pageSize).map((r) => attachEngagement(r));
  return { total, list };
}

export async function mockToggleIntelLike(
  param: IntelligenceFeedEngagementParam
): Promise<IntelligenceFeedEngagementState> {
  const id = param.intelligenceId?.trim();
  if (!id) {
    return { liked: false, collected: false };
  }
  if (mockLikedIds.has(id)) {
    mockLikedIds.delete(id);
  } else {
    mockLikedIds.add(id);
  }
  return {
    liked: mockLikedIds.has(id),
    collected: mockCollectedIds.has(id),
  };
}

export async function mockToggleIntelCollect(
  param: IntelligenceFeedEngagementParam
): Promise<IntelligenceFeedEngagementState> {
  const id = param.intelligenceId?.trim();
  if (!id) {
    return { liked: false, collected: false };
  }
  if (mockCollectedIds.has(id)) {
    mockCollectedIds.delete(id);
  } else {
    mockCollectedIds.add(id);
  }
  return {
    liked: mockLikedIds.has(id),
    collected: mockCollectedIds.has(id),
  };
}
